/**
 * Posições · Comparador (migrada do Streamlit; equivalência em
 * backend/tests_paginas/test_comparador.py).
 *
 * Compara as posições de uma carteira em duas datas. Na URL: carteira e datas (vazio =
 * as duas mais recentes). A lista de carteiras vem do servidor, já filtrada por
 * `portfolios_visiveis`.
 */
import { Alert, Loader, Select, SimpleGrid, Text } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ErroApi, apiGet, type Envelope } from '../../api/cliente';
import { AvisosApi } from '../../components/AvisosApi';
import { BarraFiltros, GrupoFiltros, LARGURA } from '../../components/Filtros';
import { Grafico, type GraficoApi } from '../../components/Grafico';
import { Kpi, type KpiApi } from '../../components/Kpi';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { formatarData } from '../../lib/formatos';
import { useFiltrosUrl } from '../../lib/url-state';

const CAMINHO = '/carteiras-administradas';

interface Movimento {
  n: number;
  tabela: TabelaApi | null;
}
interface DadosComparador {
  portfolio: string;
  datas: string[];
  aviso?: string;
  d1?: string;
  d2?: string;
  kpis?: KpiApi[];
  alocacao?: { tabela: TabelaApi; grafico: GraficoApi };
  movimentacoes?: { adicionadas: Movimento; removidas: Movimento; alteradas: Movimento };
  completas?: TabelaApi;
  emissores?: TabelaApi | null;
}

function Movimentos({ titulo, m, vazio }: { titulo: string; m: Movimento; vazio: string }) {
  return (
    <Secao titulo={`${titulo} (${m.n})`}>
      {m.tabela ? (
        <TabelaDados tabela={m.tabela} alturaAutomatica />
      ) : (
        <Text size="sm" c="dimmed">
          {vazio}
        </Text>
      )}
    </Secao>
  );
}

export function Comparador() {
  const [filtros, definir] = useFiltrosUrl({ portfolio: '', d1: '', d2: '' });
  const carteiras = useQuery({
    queryKey: ['carteiras.visiveis'],
    queryFn: ({ signal }) =>
      apiGet<Envelope<{ carteiras: string[] }>>(`${CAMINHO}/carteiras`, {}, signal),
    staleTime: 60 * 60 * 1000,
  });
  const consulta = useQuery({
    queryKey: ['carteiras.comparador', filtros],
    queryFn: ({ signal }) =>
      apiGet<Envelope<DadosComparador>>(
        `${CAMINHO}/comparador`,
        { portfolio: filtros.portfolio, d1: filtros.d1, d2: filtros.d2 },
        signal,
      ),
    enabled: !!filtros.portfolio,
    placeholderData: keepPreviousData,
  });
  const dados = consulta.data?.data;
  const datas = (dados?.datas ?? []).map((d) => ({ value: d, label: formatarData(d) }));

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Comparador"
        descricao="Posições de uma carteira em duas datas: AUM, alocação por classe, movimentações e emissores de renda fixa."
        dadosAte={consulta.data?.meta.dados_ate}
        fonte="Fibery (Posição Consolidada, Ativos)"
      />
      <BarraFiltros carregando={carteiras.isFetching || consulta.isFetching}>
        <GrupoFiltros titulo="Carteira">
          <Select
            label="Portfolio"
            placeholder="Selecione um portfolio"
            data={carteiras.data?.data.carteiras ?? []}
            value={filtros.portfolio || null}
            onChange={(p) => definir({ portfolio: p ?? '', d1: '', d2: '' })}
            searchable
            w={LARGURA.selecao}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Datas">
          <Select
            label="Data inicial"
            data={datas}
            value={filtros.d1 || dados?.d1 || null}
            onChange={(d) => d && definir({ d1: d })}
            disabled={!datas.length}
            w={LARGURA.data}
          />
          <Select
            label="Data final"
            data={datas}
            value={filtros.d2 || dados?.d2 || null}
            onChange={(d) => d && definir({ d2: d })}
            disabled={!datas.length}
            w={LARGURA.data}
          />
        </GrupoFiltros>
      </BarraFiltros>
      <AvisosApi avisos={[carteiras.data?.meta.avisos, consulta.data?.meta.avisos]} />
      {!filtros.portfolio && <Alert color="blue">Selecione um portfolio.</Alert>}
      {consulta.isError && (
        <Alert color="red">
          {consulta.error instanceof ErroApi
            ? consulta.error.message
            : 'Não foi possível carregar as posições.'}
        </Alert>
      )}
      {filtros.portfolio && consulta.isPending && <Loader />}
      {dados?.aviso && <Alert color="yellow">{dados.aviso}</Alert>}
      {dados?.kpis && dados.alocacao && dados.movimentacoes && dados.completas && (
        <>
          <Text size="sm" c="dimmed">
            {dados.portfolio} · {formatarData(dados.d1 ?? null)} → {formatarData(dados.d2 ?? null)}
          </Text>
          <SimpleGrid cols={{ base: 2, lg: 4 }}>
            {dados.kpis.map((k) => (
              <Kpi key={k.rotulo} kpi={k} />
            ))}
          </SimpleGrid>
          <Secao
            titulo="Alocação por classe de ativo"
            legenda="% do AUM em cada data; Δ em pontos percentuais."
          >
            <SimpleGrid cols={{ base: 1, lg: 2 }}>
              <TabelaDados tabela={dados.alocacao.tabela} alturaAutomatica />
              <Grafico grafico={dados.alocacao.grafico} />
            </SimpleGrid>
          </Secao>
          <SimpleGrid cols={{ base: 1, xl: 3 }} spacing="lg">
            <Movimentos
              titulo="Adicionadas"
              m={dados.movimentacoes.adicionadas}
              vazio="Nenhuma posição adicionada."
            />
            <Movimentos
              titulo="Removidas"
              m={dados.movimentacoes.removidas}
              vazio="Nenhuma posição removida."
            />
            <Movimentos
              titulo="Alteradas significativamente"
              m={dados.movimentacoes.alteradas}
              vazio="Nenhuma alteração significativa na quantidade (> 0,5%)."
            />
          </SimpleGrid>
          <Secao
            titulo="Posições completas"
            legenda="Saldo (R$) e % do AUM de cada ativo nas duas datas."
          >
            <TabelaDados tabela={dados.completas} altura={420} />
          </Secao>
          {dados.emissores && (
            <Secao
              titulo="Emissores de renda fixa"
              legenda="% do AUM por emissor (devedor, quando houver)."
            >
              <TabelaDados tabela={dados.emissores} alturaAutomatica />
            </Secao>
          )}
        </>
      )}
    </Pagina>
  );
}
